/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { CloseOutlined } from '@ant-design/icons'
import { Modal, Tag } from 'antd'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Text from 'src/components/ts-components/Text'
import Button from 'src/components/ts-components/Button'
import VideographerSelectContent from './VideographerSelectContent'
import VideographerModal, {
  VideographerModalRef,
} from 'src/pages/Game/VideoAssignment/VideographerModal'

const modalStyles = {
  header: {
    backgroundColor: colors.SECONDARY_300,
    paddingLeft: '16px',
    paddingRight: '16px',
  },
  body: {
    borderBottom: `1px solid ${colors.SOFT_STEEL}`,
    borderTop: `1px solid ${colors.SOFT_STEEL}`,
    backgroundColor: colors.SECONDARY_300,
    padding: '16px',
  },
  content: {
    backgroundColor: colors.SECONDARY_300,
    paddingTop: '8px',
    paddingBottom: '16px',
    paddingLeft: '0',
    paddingRight: '0',
  },
  wrapper: {
    zIndex: 10001,
  },
  mask: {
    backdropFilter: 'blur(2px)',
    backgroundColor: 'rgba(6, 10, 16, 0.8)',
    zIndex: 10000,
  },
  footer: {
    paddingLeft: '16px',
    paddingRight: '16px',
  },
}

interface VideographerSelectModalProps {
  isOpen: boolean
  onClose: () => void
  gameId: string
  rosterId: string
  teamId: string
  teamName: string
  homeTeamImage?: string | null
  visitorTeamImage?: string | null
  homeRosterId?: string | null
  visitorRosterId?: string | null
  homeTeamName?: string
  visitorTeamName?: string
  preSelectedTeam?: 'home' | 'visitor'
  readAttendanceInformation: () => void
}

const VideographerSelectModal: React.FC<VideographerSelectModalProps> = ({
  isOpen,
  onClose,
  gameId,
  rosterId,
  teamName,
  homeTeamImage,
  visitorTeamImage,
  homeRosterId,
  visitorRosterId,
  homeTeamName,
  visitorTeamName,
  preSelectedTeam,
  readAttendanceInformation,
}) => {
  const inviteModalRef = useRef<VideographerModalRef>(null)
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)

  const handleInviteClick = useCallback(() => {
    setShowInviteModal(true)
  }, [])

  useEffect(() => {
    if (showInviteModal) {
      inviteModalRef.current?.openModal()
    }
  }, [showInviteModal])

  const handleInviteClose = useCallback(() => {
    setShowInviteModal(false)
    setRefreshKey((prev) => prev + 1)
  }, [])

  const handleInviteSuccess = useCallback(() => {
    setShowInviteModal(false)
    setRefreshKey((prev) => prev + 1)
    readAttendanceInformation()
  }, [readAttendanceInformation])

  const handleClose = useCallback(() => {
    readAttendanceInformation()
    onClose()
  }, [readAttendanceInformation, onClose])

  const closeIcon = useMemo(() => {
    return (
      <CloseOutlined
        css={css`
          color: ${colors.SOLITUDE};
          font-size: 16px;
        `}
      />
    )
  }, [])

  const header = useMemo(() => {
    return (
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 12px;
        `}
      >
        <Text variant='heading3' weight='semiBold' color={colors.WHITE}>
          Choose Videographer(s)
        </Text>
        <Tag color='gold'>Beta</Tag>
      </div>
    )
  }, [])

  const footer = useMemo(() => {
    return (
      <div
        css={css`
          display: flex;
          justify-content: flex-end;
          width: 50%;
          margin-left: auto;
        `}
      >
        <Button text='Save' onClick={handleClose} variant='primary' />
      </div>
    )
  }, [handleClose])

  if (!isOpen) {
    return null
  }

  return (
    <>
      <Modal
        open={isOpen && !showInviteModal}
        onCancel={handleClose}
        width={500}
        centered
        styles={modalStyles}
        maskClosable={false}
        footer={footer}
        title={header}
        closeIcon={closeIcon}
      >
        <VideographerSelectContent
          gameId={gameId}
          rosterId={rosterId}
          onInviteClick={handleInviteClick}
          refreshKey={refreshKey}
        />
      </Modal>

      {showInviteModal && (
        <VideographerModal
          ref={inviteModalRef}
          gameId={gameId}
          homeRosterId={
            homeRosterId || (preSelectedTeam === 'home' ? rosterId : null)
          }
          visitorRosterId={
            visitorRosterId || (preSelectedTeam === 'visitor' ? rosterId : null)
          }
          homeTeamName={homeTeamName || teamName}
          visitorTeamName={visitorTeamName || teamName}
          onSuccess={handleInviteSuccess}
          onModalClose={handleInviteClose}
          homeTeamImage={homeTeamImage}
          visitorTeamImage={visitorTeamImage}
          preSelectedTeam={preSelectedTeam}
        />
      )}
    </>
  )
}

export default VideographerSelectModal
